import { IRoute } from "../lib/navigation-types";
import { ACCESS_TYPES, ATTRIBUTE_ROLES } from "./constants";
import { Flags } from "../feature-flags/flags";

export const AGENTS_ROUTE: IRoute = {
    name: "agents",
    title: "Virtual Teammates",
    actions: [ATTRIBUTE_ROLES.USERS.READ],
    context: [{ scopedAccess: ACCESS_TYPES.SUPER_ADMIN }],
    featureFlag: { flag: Flags.ENABLE_VIRTUAL_AGENT_USERS },
    priorityOrder: 9,
    menuItem: {
        listOrder: 1.5,
        title: "Virtual Teammates",
        icon: "virtual-teammate",
        href: "/agents",
        tooltip: "View and manage virtual teammates in your organization.",
        group: "User Management"
    }
};

export const PRODUCT_ACCESS_ROUTE: IRoute = {
    name: "products",
    title: "Products",
    pageDescription:
        "Product Access empowers you to oversee and administer users on a product-specific level. With this feature, you can assign roles directly to users for each product you're associated with, ensuring precise access levels. Additionally, you have the capability to extend invitations to users, granting them the appropriate level of access required for their involvement.",
    actions: [ATTRIBUTE_ROLES.PRODUCTS.READ],
    featureFlag: { flag: Flags.ENABLE_ACCESS_MANAGEMENT },
    priorityOrder: 1, // Original priority
    featureFlagPriority: {
        [Flags.ENABLE_USERS_DEFAULT_LANDING_PAGE]: 3 // When flag is ON, make it priority 3
    },
    tabs: [
        {
            title: "Products",
            route: `/access/products`,
            actions: [ATTRIBUTE_ROLES.PRODUCTS.READ]
        }
    ],
    shouldHideIfUserIsBillingAdminOnly: true,
    subRoutes: [
        {
            name: "instances",
            title: "<productName>",
            path: "/access/products/<productId>",
            priorityOrder: 1,
            tabs: [
                {
                    title: "Instances",
                    route: `/access/products/<productId>/instances`,
                    actions: [ATTRIBUTE_ROLES.PRODUCTS.READ],
                    context: [{ productId: "<productId>" }]
                },
                {
                    title: "Users",
                    route: `/access/products/<productId>/users`,
                    actions: [ATTRIBUTE_ROLES.USERS.READ],
                    context: [{ productId: "<productId>" }]
                }
            ],
            subRoutes: [
                {
                    name: "instance",
                    title: "<instanceName>",
                    path: "/access/products/<productId>/instances/<instanceId>",
                    priorityOrder: 1,
                    tabs: [
                        {
                            title: "Projects",
                            route: `/access/products/<productId>/instances/<instanceId>/projects`,
                            showForProduct: process.env.REACT_APP_EXPERIMENTATION_PRODUCT_ID
                        },
                        {
                            title: "Users",
                            route: `/access/products/<productId>/instances/<instanceId>/users`,
                            actions: [ATTRIBUTE_ROLES.USERS.READ],
                            context: [{ instanceId: "<instanceId>" }]
                        },
                        {
                            title: "Details",
                            route: `/access/products/<productId>/instances/<instanceId>/details`,
                            actions: [ATTRIBUTE_ROLES.INSTANCE_DETAILS.UPDATE]
                        },
                        {
                            title: "Teams",
                            route: `/access/products/<productId>/instances/<instanceId>/teams`,
                            actions: [ATTRIBUTE_ROLES.GROUPS.READ],
                            context: [{ instanceId: "<instanceId>" }],
                            featureFlag: { flag: Flags.ENABLE_TEAM_GROUPS },
                            productFlagVariable: "eligible_product_ids"
                        }
                    ],
                    subRoutes: [
                        {
                            name: "projects",
                            path: "/access/products/<productId>/instances/<instanceId>/projects/<projectId>",
                            priorityOrder: 1
                        }
                    ]
                }
            ]
        }
    ],
    menuItem: {
        listOrder: 2,
        title: "Products",
        icon: "grid",
        href: "/access",
        tooltip: "Manage users by product, assign roles, and invite new users with the right permissions.",
        group: "User Management"
    }
};

export const USER_MANAGEMENT_ROUTE = {
    name: "users",
    title: "Users",
    actions: [ATTRIBUTE_ROLES.USERS.UPDATE],
    priorityOrder: 3, // Original priority
    featureFlagPriority: {
        [Flags.ENABLE_USERS_DEFAULT_LANDING_PAGE]: 1 // When flag is ON, make it priority 1
    },
    context: [
        { scopedAccess: ACCESS_TYPES.SUPER_ADMIN },
        { scopedAccess: ACCESS_TYPES.PRODUCT },
        { scopedAccess: ACCESS_TYPES.INSTANCE }
    ],
    tabs: [
        { title: "Users", route: "/user-management/users" },
        {
            title: "Invitations",
            route: "/user-management/invitations",
            featureFlag: { flag: Flags.SHOW_INVITATIONS },
            actions: [
                ATTRIBUTE_ROLES.INVITATIONS.CREATE,
                ATTRIBUTE_ROLES.INVITATIONS.READ,
                ATTRIBUTE_ROLES.INVITATIONS.UPDATE
            ]
        },
        {
            title: "Uploads",
            route: "/user-management/uploads",
            featureFlag: { flag: Flags.ENABLE_BULK_DISPLAY },
            actions: [ATTRIBUTE_ROLES.USERS.READ],
            context: [{ scopedAccess: ACCESS_TYPES.SUPER_ADMIN }]
        }
    ],
    menuItem: {
        listOrder: 1,
        title: "Users",
        icon: "user",
        tooltip: "View and manage all users in your organization.",
        href: "/user-management",
        support: true,
        group: "User Management"
    }
};

export const GROUP_ACCESS_ROUTE = {
    name: "groups",
    title: "Group Access",
    pageDescription:
        "Group Access facilitates the allocation of product permissions based on groups, streamlining the process of granting access to multiple individuals simultaneously. This functionality empowers you to implement intricate combinations of permissions across various user groups, ensuring efficient management of access rights across your organization.",
    actions: [ATTRIBUTE_ROLES.GROUPS.UPDATE, ATTRIBUTE_ROLES.GROUPS.MANAGE],
    priorityOrder: 4,
    context: [{ scopedAccess: ACCESS_TYPES.SUPER_ADMIN }],
    menuItem: {
        listOrder: 3,
        title: "Groups",
        icon: "user-group",
        href: "/groups",
        tooltip: "Manage product access for groups to streamline permissions for multiple users.",
        group: "Access Setup"
    }
};

export const ROLES_ROUTE: IRoute = {
    name: "roles",
    title: "Roles",
    actions: [ATTRIBUTE_ROLES.ROLES.UPDATE, ATTRIBUTE_ROLES.ROLES.CREATE],
    context: [
        { scopedAccess: ACCESS_TYPES.SUPER_ADMIN },
        { scopedAccess: ACCESS_TYPES.PRODUCT },
        { scopedAccess: ACCESS_TYPES.INSTANCE }
    ],
    featureFlag: { flag: Flags.SHOW_ROLES_SIDEBAR_NAVIGATION },
    priorityOrder: 6,
    menuItem: {
        listOrder: 5,
        title: "Roles",
        icon: "shield-check",
        href: "/roles",
        tooltip: "Create and manage custom roles and responsibilities across your organization.",
        group: "Access Setup"
    }
};

export const USAGE_ROUTE: IRoute = {
    name: "usage",
    title: "Usage & Billing",
    actions: [ATTRIBUTE_ROLES.BILLING.READ],
    context: [{ scopedAccess: ACCESS_TYPES.SUPER_ADMIN }],
    featureFlag: { flag: Flags.SHOW_USAGE_AND_BILLING },
    priorityOrder: 7,
    menuItem: {
        listOrder: 7,
        title: "Usage & Billing",
        icon: "chart-mixed",
        href: "/usage",
        tooltip: "Track subscription usage and manage billing details.",
        group: "Settings"
    }
};

export const SUPPORT_ROUTE: IRoute = {
    name: "support",
    title: "Support",
    priorityOrder: 2,
    featureFlag: { flag: Flags.ENABLE_SUPPORT_FEATURES },
    actions: [ATTRIBUTE_ROLES.SETTINGS.UPDATE],
    context: [{ scopedAccess: ACCESS_TYPES.SUPER_ADMIN }],
    tabs: [
        { title: "Access", route: "/support/access" },
        { title: "Account Activation", route: "/support/account-activation" },
        { title: "User Home Organization", route: "/support/user-migration" },
        {
            title: "View User Permissions",
            route: "/support/user-permissions",
            featureFlag: { flag: Flags.ENABLE_INSTANCE_PERMISSIONS_TAB }
        },
        {
            title: "Master Dashboard",
            route: "/support/master-dashboard",
            featureFlag: { flag: Flags.ENABLE_MASTER_DASHBOARD }
        },
        {
            title: "Developer Settings",
            route: "/support/developer-settings",
            featureFlag: { flag: Flags.ENABLE_DEVELOPER_SETTINGS }
        },
        { title: "Usage & Billing", route: "/support/usage", featureFlag: { flag: Flags.SHOW_USAGE_AND_BILLING } }
    ],
    menuItem: {
        listOrder: 6,
        title: "Support",
        icon: "rocket",
        href: "/support",
        support: true,
        tooltip: "Access support tools for managing user accounts and granting organization access.",
        group: "Support"
    }
};

export const SETTINGS_ROUTE: IRoute = {
    name: "settings",
    title: "Settings",
    priorityOrder: 8,
    actions: [ATTRIBUTE_ROLES.SETTINGS.UPDATE],
    context: [{ scopedAccess: ACCESS_TYPES.SUPER_ADMIN }],
    subRoutes: [
        {
            path: "/settings/authentication",
            title: "Authentication",
            name: "authentication",
            priorityOrder: 1,
            tabs: [
                {
                    title: "SSO",
                    route: "/settings/authentication/sso",
                    actions: [ATTRIBUTE_ROLES.SETTINGS.UPDATE],
                    context: [{ scopedAccess: ACCESS_TYPES.SUPER_ADMIN }]
                },
                {
                    title: "Domains / Dynamic Provisioning",
                    route: "/settings/authentication/domains",
                    actions: [ATTRIBUTE_ROLES.SETTINGS.UPDATE],
                    context: [{ scopedAccess: ACCESS_TYPES.SUPER_ADMIN }]
                },
                {
                    title: "SCIM",
                    route: "/settings/authentication/scim",
                    featureFlag: { flag: Flags.ENABLE_SCIM_UI, offToShow: false },
                    actions: [ATTRIBUTE_ROLES.SETTINGS.UPDATE],
                    context: [{ scopedAccess: ACCESS_TYPES.SUPER_ADMIN }]
                },
                {
                    title: "MFA",
                    route: "/settings/authentication/mfa",
                    featureFlag: { flag: Flags.SSO_SUPPORTED_LOCAL_LOGIN, offToShow: true },
                    actions: [ATTRIBUTE_ROLES.SETTINGS.UPDATE],
                    context: [{ scopedAccess: ACCESS_TYPES.SUPER_ADMIN }],
                    requiresDisabledSso: true
                }
            ]
        },
        {
            name: "billing-notifications",
            title: "Billing Notifications",
            path: "/settings/notifications",
            priorityOrder: 2
        },
        {
            name: "genai",
            title: "Opal",
            path: "/settings/genai",
            priorityOrder: 3,
            featureFlag: { flag: Flags.ENABLE_OPAL_AUTO_PROVISIONING },
            pageDescription:
                "Opal settings allow you to manage the use of AI-driven features across your organization. Enabling Opal unlocks advanced capabilities, including chat, agents, and additional features that help streamline tasks and enhance productivity across Optimizely products.",
            tabs: [
                {
                    title: "Organization",
                    route: "/settings/genai",
                    featureFlag: { flag: Flags.ENABLE_OPAL_AUTO_PROVISIONING }
                },
                {
                    title: "Products & Instances",
                    route: "/settings/genai/products-instances",
                    featureFlag: { flag: Flags.ENABLE_OPAL_AUTO_PROVISIONING },
                    actions: [ATTRIBUTE_ROLES.SETTINGS.UPDATE]
                }
            ]
        },
        {
            name: "organization",
            title: "Organization",
            path: "/settings/organization",
            priorityOrder: 4,
            featureFlag: { flag: Flags.ENABLE_DEVELOPER_SETTINGS },
            tabs: [
                {
                    title: "Developer Access",
                    route: "/settings/organization",
                    featureFlag: { flag: Flags.ENABLE_DEVELOPER_SETTINGS }
                }
            ]
        }
    ],
    menuItem: {
        listOrder: 8,
        title: "Settings",
        icon: "gear",
        href: "/settings",
        tooltip: "Configure organization settings and preferences.",
        group: "Settings",
        subItems: [
            {
                listOrder: 1,
                title: "Authentication",
                href: "/settings/authentication",
                tooltip: "Manage SSO, SCIM, and authentication settings for your organization."
            },
            {
                listOrder: 2,
                title: "Notifications",
                href: "/settings/notifications",
                tooltip: "Configure billing notification preferences for your organization."
            },
            {
                listOrder: 3,
                title: "Opal",
                href: "/settings/genai",
                tooltip: "Manage Opal settings and provisioning for your organization."
            },
            {
                listOrder: 4,
                title: "Organization",
                href: "/settings/organization",
                tooltip: "View your organization's developer access and vendor identity.",
                featureFlag: { flag: Flags.ENABLE_DEVELOPER_SETTINGS }
            }
        ]
    }
};

export const TEAMS_ROUTE: IRoute = {
    name: "teams",
    title: "Teams Access",
    actions: [ATTRIBUTE_ROLES.GROUPS.READ],
    context: [],
    featureFlag: { flag: Flags.ENABLE_TEAM_GROUPS },
    priorityOrder: 5,
    menuItem: {
        listOrder: 4,
        title: "Teams",
        icon: "users",
        href: "/teams",
        tooltip: "Manage teams for eligible products.",
        group: "Access Setup"
    }
};

export const ROUTE_VIEW_MAPPINGS: {
    [index: string]: IRoute;
} = {
    "/access": PRODUCT_ACCESS_ROUTE,
    "/user-management": USER_MANAGEMENT_ROUTE,
    "/groups": GROUP_ACCESS_ROUTE,
    "/roles": ROLES_ROUTE,
    "/usage": USAGE_ROUTE,
    "/support": SUPPORT_ROUTE,
    "/settings": SETTINGS_ROUTE,
    "/teams": TEAMS_ROUTE,
    "/agents": AGENTS_ROUTE
};
